import { useEventConfig } from '@/eventConfig/useEventConfig.ts';
import { useRef, useState } from 'react';
import { isNil } from 'ramda';
import { httpClient } from '@/httpClient/axios.ts';
import { isOnline } from '@/util/isOnline.ts';
import * as Sentry from '@sentry/browser';

interface UseOnlineStatusApi {
  onlineStatus: OnlineStatus;
  onlineCheck: () => Promise<void>;
}

export type OnlineStatus = 'unknown' | 'online' | 'no-backend' | 'offline';

export const useOnlineStatus = ({ spinnerDuration = 1000 }): UseOnlineStatusApi => {
  const { eventConfig } = useEventConfig();
  const [onlineStatus, setOnlineStatus] = useState<OnlineStatus>('unknown');
  const retry401ref = useRef<boolean>(false);

  const onlineCheck = () => {
    setOnlineStatus('unknown');

    if (!isOnline()) {
      setTimeout(() => {
        setOnlineStatus('offline');
      }, spinnerDuration);
    } else {
      if (!isNil(eventConfig)) {
        const url = eventConfig.meta.eventStatusURL;
        return httpClient
          .head(url)
          .then(() => {
            setTimeout(() => {
              setOnlineStatus('online');
            }, spinnerDuration);
          })
          .catch((e) => {
            // On local dev, we get 404, but want to stay online
            // if (isLocalDev()) {
            //   setTimeout(() => {
            //     setOnlineStatus('online');
            //   }, spinnerDuration);
            // } else {

            if (e?.response && e?.response?.status === 401) {
              // No "Device" found

              if (!retry401ref.current) {
                retry401ref.current = true;

                // Fetching the original document from the ereg-backend gets an
                // updated ereg-device-token cookie. This might solve the problem
                // otherwise we have to report an error to the user.
                fetch(window.location.href).then(() => {
                  return onlineCheck();
                });
              }

              if (retry401ref.current) {
                setTimeout(() => {
                  setOnlineStatus('no-backend');
                }, spinnerDuration);
                return;
              }
            }

            console.error('Online check failed, because the backend had an Error');
            Sentry.captureException(e);
            setTimeout(() => {
              setOnlineStatus('no-backend');
            }, spinnerDuration);
          });
      }
    }
    return Promise.resolve();
  };

  return { onlineStatus, onlineCheck };
};
